import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Filter, MapPin, Award, TrendingUp, Users, CheckCircle, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AlertBanner from '../components/common/AlertBanner';
import ProgressBar from '../components/common/ProgressBar';

const NGOMatchingPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [matchSuggestions, setMatchSuggestions] = useState([]);
  const [allNGOs, setAllNGOs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [minScore, setMinScore] = useState(50);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedNGO, setSelectedNGO] = useState(null);

  useEffect(() => {
    fetchMatchSuggestions();
  }, [minScore]);

  const fetchMatchSuggestions = async () => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${apiUrl}/api/matching/suggestions?limit=50&minScore=${minScore}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      setMatchSuggestions(data.suggestions || []);
      setAllNGOs(data.suggestions || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setLoading(false);
    }
  };

  const filteredNGOs = allNGOs.filter(ngo => {
    const matchesSearch = ngo.ngoName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesState = !selectedState || ngo.states?.includes(selectedState);
    const matchesCategory = !selectedCategory || ngo.focusAreas?.includes(selectedCategory);
    return matchesSearch && matchesState && matchesCategory;
  });

  if (loading) {
    return <LoadingSpinner fullScreen size="xl" />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Find NGO Partners</h1>
          <p className="text-gray-600 mt-1">Discover verified NGOs matched to your CSR goals</p>
        </div>

        {/* Top Match Banner */}
        {matchSuggestions.length > 0 && matchSuggestions[0].matchScore >= 80 && (
          <AlertBanner
            type="success"
            message={`Top Match Found! ${matchSuggestions[0].ngoName} has ${matchSuggestions[0].matchScore.toFixed(0)}% compatibility with your goals.`}
            className="mb-6"
          />
        )}

        {/* Search & Filters */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search NGOs by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            {/* Filter Toggle */}
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Filter className="w-5 h-5" />
              Filters
            </Button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">All States</option>
                  <option value="Maharashtra">Maharashtra</option>
                  <option value="Karnataka">Karnataka</option>
                  <option value="Tamil Nadu">Tamil Nadu</option>
                  <option value="Gujarat">Gujarat</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Focus Area</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">All Categories</option>
                  <option value="Promoting education">Education</option>
                  <option value="Promoting gender equality and empowering women">Women Empowerment</option>
                  <option value="Ensuring environmental sustainability">Environment</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Min Match Score</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={minScore}
                  onChange={(e) => setMinScore(parseInt(e.target.value))}
                  className="w-full"
                />
                <p className="text-sm text-gray-600 mt-1">{minScore}%</p>
              </div>
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-gray-600">
            Found <span className="font-semibold text-gray-900">{filteredNGOs.length}</span> NGOs matching your criteria
          </p>
        </div>

        {/* NGO Cards Grid */}
        {filteredNGOs.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No NGOs Found</h3>
            <p className="text-gray-600">Try adjusting your filters to see more results</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredNGOs.map((ngo) => (
              <NGOCard
                key={ngo.ngoId}
                ngo={ngo}
                onClick={() => setSelectedNGO(ngo)}
              />
            ))}
          </div>
        )}

        {/* NGO Detail Modal */}
        {selectedNGO && (
          <NGODetailModal
            ngo={selectedNGO}
            onClose={() => setSelectedNGO(null)}
            onConnect={() => {
              navigate('/projects/create', { state: { selectedNGO: selectedNGO.ngoId } });
            }}
          />
        )}
      </div>
    </div>
  );
};

// NGO Card Component
const NGOCard = ({ ngo, onClick }) => {
  const getScoreColor = (score) => {
    if (score >= 80) return '#10B981'; // Green
    if (score >= 60) return '#F59E0B'; // Yellow
    return '#EF4444'; // Red
  };

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all cursor-pointer overflow-hidden"
    >
      {/* Match Score Banner */}
      <div
        className="p-4 text-white"
        style={{ background: getScoreColor(ngo.matchScore) }}
      >
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold">Match Score</span>
          <span className="text-2xl font-bold">{ngo.matchScore.toFixed(0)}%</span>
        </div>
      </div>

      <div className="p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-2">{ngo.ngoName}</h3>

        {/* Credibility Score */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-gray-600">Credibility Score</span>
            <span className="font-semibold text-teal-600">{ngo.credibilityScore.toFixed(0)}/100</span>
          </div>
          <ProgressBar progress={ngo.credibilityScore} showLabel={false} height="h-2" />
        </div>

        {/* Focus Areas */}
        <div className="mb-4">
          <p className="text-sm text-gray-500 mb-2">Focus Areas</p>
          <div className="flex flex-wrap gap-2">
            {ngo.focusAreas?.slice(0, 2).map((area, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium"
              >
                {area.split(' ').slice(0, 2).join(' ')}
              </span>
            ))}
            {ngo.focusAreas?.length > 2 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                +{ngo.focusAreas.length - 2} more
              </span>
            )}
          </div>
        </div>

        {/* Location */}
        <div className="mb-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <MapPin className="w-4 h-4" />
            <span>{ngo.states?.slice(0, 2).join(', ')}</span>
            {ngo.states?.length > 2 && <span>+{ngo.states.length - 2}</span>}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
          <div>
            <p className="text-xs text-gray-500 mb-1">Projects</p>
            <p className="text-sm font-semibold text-gray-900">{ngo.projectsCompleted}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Match Criteria</p>
            <div className="flex gap-1">
              {ngo.matchCriteria.geographyMatch > 20 && (
                <div className="w-2 h-2 bg-teal-500 rounded-full" title="Geography Match" />
              )}
              {ngo.matchCriteria.causeAreaMatch > 20 && (
                <div className="w-2 h-2 bg-blue-500 rounded-full" title="Cause Match" />
              )}
              {ngo.matchCriteria.credibilityScore > 15 && (
                <div className="w-2 h-2 bg-green-500 rounded-full" title="High Credibility" />
              )}
            </div>
          </div>
        </div>

        <Button
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className="w-full mt-4 text-white"
          style={{ background: '#14B8A6' }}
        >
          View Details
        </Button>
      </div>
    </div>
  );
};

// NGO Detail Modal
const NGODetailModal = ({ ngo, onClose, onConnect }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">{ngo.ngoName}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Match Analysis */}
          <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-6 mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Match Analysis</h3>
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Geography Match</span>
                  <span className="text-sm font-bold text-teal-600">{ngo.matchCriteria.geographyMatch.toFixed(0)}/30</span>
                </div>
                <ProgressBar progress={(ngo.matchCriteria.geographyMatch / 30) * 100} showLabel={false} height="h-2" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Cause Area Match</span>
                  <span className="text-sm font-bold text-teal-600">{ngo.matchCriteria.causeAreaMatch.toFixed(0)}/30</span>
                </div>
                <ProgressBar progress={(ngo.matchCriteria.causeAreaMatch / 30) * 100} showLabel={false} height="h-2" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Budget Fit</span>
                  <span className="text-sm font-bold text-teal-600">{ngo.matchCriteria.budgetFitMatch.toFixed(0)}/20</span>
                </div>
                <ProgressBar progress={(ngo.matchCriteria.budgetFitMatch / 20) * 100} showLabel={false} height="h-2" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Credibility Score</span>
                  <span className="text-sm font-bold text-teal-600">{ngo.matchCriteria.credibilityScore.toFixed(0)}/20</span>
                </div>
                <ProgressBar progress={(ngo.matchCriteria.credibilityScore / 20) * 100} showLabel={false} height="h-2" />
              </div>
            </div>
          </div>

          {/* Credibility Details */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Credibility Score</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-2xl font-bold text-teal-600">{ngo.credibilityScore.toFixed(0)}/100</span>
                <Award className="w-8 h-8 text-teal-500" />
              </div>
              <p className="text-sm text-gray-600">
                This NGO has a {ngo.credibilityScore >= 75 ? 'high' : ngo.credibilityScore >= 50 ? 'good' : 'moderate'} credibility score based on documentation, financial health, past performance, and governance.
              </p>
            </div>
          </div>

          {/* Focus Areas */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Focus Areas</h3>
            <div className="flex flex-wrap gap-2">
              {ngo.focusAreas?.map((area, index) => (
                <span
                  key={index}
                  className="px-3 py-2 bg-teal-100 text-teal-700 rounded-lg text-sm font-medium"
                >
                  {area}
                </span>
              ))}
            </div>
          </div>

          {/* Geographic Reach */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Geographic Reach</h3>
            <div className="flex flex-wrap gap-2">
              {ngo.states?.map((state, index) => (
                <span
                  key={index}
                  className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium flex items-center gap-2"
                >
                  <MapPin className="w-4 h-4" />
                  {state}
                </span>
              ))}
            </div>
          </div>

          {/* Performance Stats */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Performance</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{ngo.projectsCompleted}</p>
                <p className="text-sm text-gray-600 mt-1">Projects Completed</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{ngo.matchScore.toFixed(0)}%</p>
                <p className="text-sm text-gray-600 mt-1">Overall Match</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center gap-1">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <p className="text-sm font-semibold text-gray-900">Verified</p>
                </div>
                <p className="text-sm text-gray-600 mt-1">NGO Status</p>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1"
            >
              Close
            </Button>
            <Button
              onClick={onConnect}
              className="flex-1 text-white"
              style={{ background: '#14B8A6' }}
            >
              Create Project with this NGO
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NGOMatchingPage;
